// frontend/src/components/admin/AdminOrders.js - FIXED VERSION with proper status validation

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Card } from '../../components/ui/card';
import {
    Eye,
    Trash2,
    Package,
    RefreshCw,
    Search,
    Filter,
    AlertTriangle,
    ArrowLeft,
    User,
    Truck,
    CheckCircle,
    Clock,
    X,
    Info
} from 'lucide-react';
import axios from 'axios';

const AdminOrders = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleting, setDeleting] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [updateLoading, setUpdateLoading] = useState({});
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [newStatus, setNewStatus] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        fetchOrders();
    }, []);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            const { data } = await axios.get('/api/v1/admin/orders-list', {
                withCredentials: true
            });
           
            if (data.success) {
                // Fix for correctly displaying per pack price calculations
                const processedOrders = data.orders.map(order => {
                    // Calculate total price correctly considering all price types
                    let calculatedTotal = 0;
                   
                    if (order.orderItems && order.orderItems.length > 0) {
                        calculatedTotal = order.orderItems.reduce((total, item) => {
                            let itemPrice = item.price; // Default price
                           
                            // Use the appropriate price based on price type
                            if (item.priceType === 'perDozen') {
                                itemPrice = item.dozenPrice || item.price;
                            } else if (item.priceType === 'perPack') {
                                itemPrice = item.packPrice || item.price;
                            }
                           
                            return total + (itemPrice * item.quantity);
                        }, 0);
                    }
                   
                    // If the calculated total differs significantly from the stored total,
                    // log a warning but use the stored total - we don't want to change DB data here
                    if (Math.abs(calculatedTotal - order.totalPrice) > 1) { // Allow for minor floating point differences
                        console.warn(`Order ${order.orderId} has price discrepancy. Calculated: ${calculatedTotal}, Stored: ${order.totalPrice}`);
                    }
                   
                    return order;
                });
               
                setOrders(processedOrders);
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to fetch orders');
        } finally {
            setLoading(false);
        }
    };

    // FIXED: Proper status validation function
    const canUpdateToStatus = (order, targetStatus) => {
        const currentStatus = order.orderStatus;
        
        // If order is delivered or cancelled, no updates allowed
        if (currentStatus === 'Order Delivered' || currentStatus === 'Cancelled') {
            return { allowed: false, reason: 'Order is already finalized' };
        }

        // Define valid status transitions
        switch (targetStatus) {
            case 'Order Placed':
                // Can't go back to placed from any other status
                return { allowed: false, reason: 'Cannot revert to placed status' };
                
            case 'Order Packed':
                if (currentStatus === 'Order Placed') {
                    return { allowed: true };
                } else if (currentStatus === 'Order Packed') {
                    return { allowed: false, reason: 'Order is already packed' };
                } else {
                    return { allowed: false, reason: 'Invalid status transition' };
                }
                
            case 'Order Out for Delivery':
                if (currentStatus === 'Order Packed') {
                    if (!order.deliveryStaff) {
                        return { 
                            allowed: false, 
                            reason: 'Delivery staff must be assigned before marking as out for delivery',
                            requiresDeliveryStaff: true
                        };
                    }
                    return { allowed: true };
                } else if (currentStatus === 'Order Out for Delivery') {
                    return { allowed: false, reason: 'Order is already out for delivery' };
                } else {
                    return { allowed: false, reason: 'Order must be packed first' };
                }
                
            case 'Order Delivered':
                if (currentStatus === 'Order Out for Delivery') {
                    return { allowed: true };
                } else if (currentStatus === 'Order Delivered') {
                    return { allowed: false, reason: 'Order is already delivered' };
                } else {
                    return { allowed: false, reason: 'Order must be out for delivery first' };
                }
                
            case 'Cancelled':
                if (currentStatus !== 'Order Delivered') {
                    return { allowed: true };
                } else {
                    return { allowed: false, reason: 'Cannot cancel delivered order' };
                }
                
            default:
                return { allowed: false, reason: 'Invalid status' };
        }
    };

    const handleStatusUpdate = async (orderId, targetStatus) => {
        const order = orders.find(o => o._id === orderId);
        if (!order) return;

        const validation = canUpdateToStatus(order, targetStatus);
        
        if (!validation.allowed) {
            if (validation.requiresDeliveryStaff) {
                toast.error(`${validation.reason}. Please go to order details to assign delivery staff.`);
            } else {
                toast.error(validation.reason);
            }
            return;
        }

        try {
            setUpdateLoading(prev => ({ ...prev, [orderId]: true }));
            
            const { data } = await axios.put(
                `/api/v1/admin/order/${orderId}`,
                { orderStatus: targetStatus },
                { withCredentials: true }
            );
            
            if (data.success) {
                toast.success('Order status updated successfully');
                fetchOrders();
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Error updating order status');
        } finally {
            setUpdateLoading(prev => ({ ...prev, [orderId]: false }));
        }
    };

    const handleDeleteOrder = async (orderId) => {
        if (window.confirm('Are you sure you want to delete this order?')) {
            try {
                setDeleting(true);
                const { data } = await axios.delete(`/api/v1/admin/order/${orderId}`, {
                    withCredentials: true
                });
               
                if (data.success) {
                    toast.success('Order deleted successfully');
                    fetchOrders();
                }
            } catch (error) {
                toast.error(error.response?.data?.message || 'Error deleting order');
            } finally {
                setDeleting(false);
            }
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Order Delivered':
                return 'bg-green-100 text-green-800 border-green-200';
            case 'Order Placed':
                return 'bg-blue-100 text-blue-800 border-blue-200';
            case 'Order Out for Delivery':
                return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'Order Packed':
                return 'bg-purple-100 text-purple-800 border-purple-200';
            case 'Payment Pending':
                return 'bg-orange-100 text-orange-800 border-orange-200';
            case 'Cancelled':
                return 'bg-red-100 text-red-800 border-red-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const getAvailableStatuses = (currentStatus) => {
        const allStatuses = [
            'Order Placed',
            'Order Packed', 
            'Order Out for Delivery',
            'Order Delivered',
            'Cancelled'
        ];
        
        // Filter out statuses that are not valid transitions
        return allStatuses.filter(status => status !== currentStatus);
    };

    const filteredOrders = orders.filter(order => {
        const matchesSearch =
            order.orderId.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.user?.email?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus =
            statusFilter === 'all' || order.orderStatus === statusFilter;
        return matchesSearch && matchesStatus;
    });

    // Enhanced Mobile order card component with status validation
    const OrderCard = ({ order }) => (
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 mb-3">
            <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                        <Package className="h-4 w-4 text-[#dc2626]" />
                        <span className="font-medium text-sm text-gray-900">{order.orderId}</span>
                    </div>
                    <div className="text-xs text-gray-600 mb-1">
                        <User className="h-3 w-3 inline mr-1" />
                        {order.user?.name}
                    </div>
                    {order.deliveryStaff && (
                        <div className="text-xs text-green-600 mb-1">
                            <Truck className="h-3 w-3 inline mr-1" />
                            Staff: {order.deliveryStaffName}
                        </div>
                    )}
                    {!order.deliveryStaff && order.orderStatus !== 'Order Placed' && order.orderStatus !== 'Order Delivered' && order.orderStatus !== 'Cancelled' && (
                        <div className="text-xs text-orange-600 mb-1">
                            <AlertTriangle className="h-3 w-3 inline mr-1" />
                            No delivery staff assigned
                        </div>
                    )}
                </div>
                <div className="flex flex-col items-end gap-1">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(order.orderStatus)}`}>
                        {order.orderStatus}
                    </span>
                    <div className="text-sm font-semibold text-[#dc2626]">₹{order.totalPrice.toFixed(2)}</div>
                </div>
            </div>

            {/* Mobile Status Update */}
            <div className="mb-3">
                <select
                    className={`w-full text-xs rounded border px-2 py-1 ${getStatusColor(order.orderStatus)} focus:outline-none focus:ring-1 focus:ring-[#dc2626]`}
                    value={order.orderStatus}
                    onChange={(e) => handleStatusUpdate(order._id, e.target.value)}
                    disabled={updateLoading[order._id] || order.orderStatus === 'Order Delivered' || order.orderStatus === 'Cancelled'}
                >
                    <option value={order.orderStatus}>{order.orderStatus}</option>
                    {getAvailableStatuses(order.orderStatus).map(status => {
                        const validation = canUpdateToStatus(order, status);
                        return (
                            <option 
                                key={status} 
                                value={status}
                                disabled={!validation.allowed}
                            >
                                {status} {!validation.allowed && validation.requiresDeliveryStaff ? '(Requires Staff)' : ''}
                            </option>
                        );
                    })}
                </select>
            </div>
           
            <div className="flex justify-between items-center">
                <div className="text-xs text-gray-500">
                    <Clock className="h-3 w-3 inline mr-1" />
                    {new Date(order.createdAt).toLocaleDateString()}
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => navigate(`/admin/order/${order._id}`)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                        title="View Details"
                    >
                        <Eye className="h-4 w-4" />
                    </button>
                    {order.orderStatus !== 'Order Delivered' && (
                        <button
                            onClick={() => handleDeleteOrder(order._id)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                            disabled={deleting}
                            title="Delete Order"
                        >
                            <Trash2 className="h-4 w-4" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <RefreshCw className="w-6 h-6 text-[#dc2626] animate-spin" />
            </div>
        );
    }

    return (
        <div className="container mx-auto px-3 py-6">
            {/* Back to Dashboard Button */}
            <div className="flex justify-end mb-4">
                <button
                    onClick={() => navigate('/admin/dashboard')}
                    className="flex items-center gap-1.5 text-gray-600 hover:text-[#dc2626] transition-colors text-sm"
                >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back to Dashboard</span>
                </button>
            </div>

            <Card className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="p-4 border-b border-gray-200">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <h2 className="text-xl font-bold text-[#dc2626] font-['Crimson_Text']">
                            Orders Management
                        </h2>
                        <div className="flex flex-col sm:flex-row gap-3">
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Search orders..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#dc2626] focus:border-transparent w-full text-xs"
                                />
                                <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
                            </div>
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="px-3 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#dc2626] focus:border-transparent text-xs"
                            >
                                <option value="all">All Status</option>
                                <option value="Order Placed">Order Placed</option>
                                <option value="Order Packed">Order Packed</option>
                                <option value="Order Out for Delivery">Out for Delivery</option>
                                <option value="Order Delivered">Delivered</option>
                                <option value="Cancelled">Cancelled</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Desktop View */}
                <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Order ID</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Delivery Staff</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredOrders.map((order) => (
                                <tr key={order._id} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <Package className="h-4 w-4 text-[#dc2626] mr-1.5" />
                                            <span className="font-medium text-xs">{order.orderId}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <div>
                                            <div className="text-xs font-medium text-gray-900">{order.user?.name}</div>
                                            <div className="text-xs text-gray-500">{order.user?.email}</div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <div className="text-xs font-medium text-[#dc2626]">₹{order.totalPrice.toFixed(2)}</div>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        {order.deliveryStaff ? (
                                            <div className="flex items-center text-green-600">
                                                <CheckCircle className="h-3 w-3 mr-1" />
                                                <span className="text-xs">{order.deliveryStaffName}</span>
                                            </div>
                                        ) : (
                                            <div className="flex items-center text-orange-600">
                                                <AlertTriangle className="h-3 w-3 mr-1" />
                                                <span className="text-xs">Not Assigned</span>
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <select
                                            className={`text-xs rounded border px-2 py-0.5 ${getStatusColor(order.orderStatus)} focus:outline-none focus:ring-1 focus:ring-[#dc2626]`}
                                            value={order.orderStatus}
                                            onChange={(e) => handleStatusUpdate(order._id, e.target.value)}
                                            disabled={updateLoading[order._id] || order.orderStatus === 'Order Delivered' || order.orderStatus === 'Cancelled'}
                                        >
                                            <option value={order.orderStatus}>{order.orderStatus}</option>
                                            {getAvailableStatuses(order.orderStatus).map(status => {
                                                const validation = canUpdateToStatus(order, status);
                                                return (
                                                    <option 
                                                        key={status} 
                                                        value={status}
                                                        disabled={!validation.allowed}
                                                    >
                                                        {status} {!validation.allowed && validation.requiresDeliveryStaff ? '(Requires Staff Assignment)' : ''}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                        {updateLoading[order._id] && (
                                            <RefreshCw className="h-3 w-3 animate-spin text-[#dc2626] ml-1 inline" />
                                        )}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <div className="flex space-x-1.5">
                                            <button
                                                onClick={() => navigate(`/admin/order/${order._id}`)}
                                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                                                title="View Order Details"
                                            >
                                                <Eye className="h-4 w-4" />
                                            </button>
                                            {order.orderStatus !== 'Order Delivered' && (
                                                <button
                                                    onClick={() => handleDeleteOrder(order._id)}
                                                    className="p-1.5 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                                                    disabled={deleting}
                                                    title="Delete Order"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Mobile View */}
                <div className="md:hidden p-3">
                    {filteredOrders.map((order) => (
                        <OrderCard key={order._id} order={order} />
                    ))}
                </div>

                {filteredOrders.length === 0 && (
                    <div className="text-center py-8">
                        <Package className="mx-auto h-10 w-10 text-gray-400" />
                        <h3 className="mt-2 text-xs font-medium text-gray-900">No Orders Found</h3>
                        <p className="mt-1 text-xs text-gray-500">
                            {searchTerm || statusFilter !== 'all' ?
                                'No orders match your search criteria.' :
                                'No orders have been placed yet.'}
                        </p>
                    </div>
                )}
            </Card>

            {/* Information Modal/Alert */}
            {filteredOrders.some(order => !order.deliveryStaff && 
                ['Order Packed', 'Order Out for Delivery'].includes(order.orderStatus)) && (
                <div className="fixed bottom-4 right-4 max-w-sm bg-yellow-50 border border-yellow-200 rounded-lg p-3 shadow-lg">
                    <div className="flex items-start">
                        <Info className="h-4 w-4 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
                        <div>
                            <p className="text-xs font-medium text-yellow-800">Notice</p>
                            <p className="text-xs text-yellow-700 mt-1">
                                Some orders require delivery staff assignment before status updates.
                                Click "View Details" to assign staff.
                            </p>
                        </div>
                        <button 
                            onClick={() => document.querySelector('.fixed.bottom-4').style.display = 'none'}
                            className="ml-2 text-yellow-600 hover:text-yellow-800"
                        >
                            <X className="h-3 w-3" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminOrders;